const NATORI_AA_FACET_ORDER = [
  'options.size',
  'meta.custom.simple_color',
  'options.color',
  'meta.shopify.size',
  'options.band',
  'options.cup',
  'price',
];

const NATORI_AA_FACET_EXCLUDE = new Set([
  'tags',
  'vendor',
  'collections',
  'collection_ids',
  'inventory_available',
  'inventory_quantity',
  'named_tags',
]);

const NATORI_AA_FACET_LABELS = {
  'options.size': 'Size',
  'meta.custom.simple_color': 'Color',
  'options.color': 'Color',
  'meta.shopify.size': 'Size',
  'meta.shopify.cup-size': 'Cup',
  'options.band': 'Band',
  'options.cup': 'Cup',
  price: 'Price',
};

const NATORI_AA_IMAGE_WIDTHS = [220, 400, 600];
const NATORI_AA_IMAGE_SIZES = '(max-width: 767px) 50vw, 220px';

const getNatoriAutocompleteProductImageUrl = (item) =>
  item?.product_image ||
  item?.image ||
  window.algoliaShopify?.helpers?.mediumImage?.(item) ||
  '';

const buildNatoriAutocompleteProductImage = (item) => {
  const renderImage = window.algoliaShopify?.helpers?.renderImage;
  if (typeof renderImage === 'function') {
    const image = renderImage(item, 400);
    if (image?.src) {
      return {
        src: image.src,
        srcset: image.srcset || '',
        sizes: image.sizes || NATORI_AA_IMAGE_SIZES,
      };
    }
  }

  const imageUrl = getNatoriAutocompleteProductImageUrl(item);
  if (!imageUrl) {
    return { src: '', srcset: '', sizes: NATORI_AA_IMAGE_SIZES };
  }

  const [path, query] = String(imageUrl).split('?');
  const cleanPath = path.replace(
    /_(pico|icon|thumb|small|compact|medium|large|grande|\d+x\d+)(?=\.(jpg|jpeg|png|webp|gif))/i,
    ''
  );
  const querySuffix = query ? `&${query}` : '';
  const buildUrl = (width) => `${cleanPath}?width=${width}${querySuffix}`;
  const srcset = NATORI_AA_IMAGE_WIDTHS.map((width) => `${buildUrl(width)} ${width}w`).join(', ');

  return {
    src: buildUrl(400),
    srcset,
    sizes: NATORI_AA_IMAGE_SIZES,
  };
};

/** InstantSearch / legacy hash filter keys used in /search?q=…#/filter:key:value */
const NATORI_AA_FILTER_KEYS = {
  'options.size': 'variant_size',
  'meta.shopify.size': 'variant_size',
  'meta.custom.simple_color': 'color',
  'options.color': 'color',
  'options.band': 'band',
  'options.cup': 'cup',
  'meta.shopify.cup-size': 'cup',
  price: 'price',
};

let natoriProductsIndexNamePromise;
let natoriAaActiveCollection = null; // { handle, title, query }
let natoriAaApi = null;
let natoriAaLastInputQuery = '';
let natoriAaHoverTimer = null;

const getNatoriProductsIndexName = () => {
  if (!natoriProductsIndexNamePromise) {
    natoriProductsIndexNamePromise = (async () => {
      const config = window.algoliaShopify?.config || {};
      const suffix = config.index_suffix ? await config.index_suffix : '';
      return `${config.index_prefix || 'shopify_'}products${suffix || ''}`;
    })();
  }

  return natoriProductsIndexNamePromise;
};

const getNatoriFacetLabel = (attribute) => {
  if (NATORI_AA_FACET_LABELS[attribute]) {
    return NATORI_AA_FACET_LABELS[attribute];
  }

  const leaf = String(attribute).split('.').pop() || attribute;
  return leaf.replace(/[_-]+/g, ' ');
};

const getNatoriFilterKey = (attribute) => {
  if (NATORI_AA_FILTER_KEYS[attribute]) {
    return NATORI_AA_FILTER_KEYS[attribute];
  }

  const raw = String(attribute || '');
  if (raw.startsWith('options.')) {
    return `variant_${raw.slice('options.'.length)}`;
  }
  if (raw.startsWith('meta.custom.')) {
    return raw.slice('meta.custom.'.length);
  }
  if (raw.startsWith('meta.shopify.')) {
    return raw.slice('meta.shopify.'.length);
  }
  return raw;
};

const getNatoriActiveSearchQuery = (fallback = '') => {
  if (natoriAaActiveCollection?.query) {
    return natoriAaActiveCollection.query;
  }
  return fallback || '';
};

const pickNatoriAutocompleteFacets = (facetsData) => {
  const selected = [];

  NATORI_AA_FACET_ORDER.forEach((attribute) => {
    if (selected.length >= 3) {
      return;
    }

    if (facetsData[attribute] && Object.keys(facetsData[attribute]).length) {
      selected.push(attribute);
    }
  });

  if (selected.length < 3) {
    Object.keys(facetsData).forEach((attribute) => {
      if (selected.length >= 3) {
        return;
      }

      if (NATORI_AA_FACET_EXCLUDE.has(attribute) || selected.includes(attribute)) {
        return;
      }

      if (Object.keys(facetsData[attribute]).length) {
        selected.push(attribute);
      }
    });
  }

  return selected;
};

const buildNatoriFacetSearchUrl = (query, attribute, value) => {
  const searchPath = window.Shopify?.routes?.search_url || '/search';
  const q = String(query || '').trim();
  const params = new URLSearchParams();
  if (q) {
    params.set('q', q);
  }

  const base = `${searchPath}?${params.toString()}`;
  if (!attribute || value == null || value === '') {
    return base;
  }

  const key = getNatoriFilterKey(attribute);
  const filterValue = String(value).replace(/^\[\"|\"\]$/g, '');
  return `${base}#/filter:${key}:${filterValue}`;
};

const getNatoriStockFilter = () => {
  const stockPolicy = window.algoliaShopify?.config?.stock_policy;

  if (stockPolicy === 'deny') {
    return 'inventory_quantity > 0';
  }

  if (stockPolicy === 'continue') {
    return 'inventory_available:true';
  }

  return '';
};

const buildNatoriAutocompleteFacetItems = (facetsData, query) => {
  const items = [];
  const attributes = pickNatoriAutocompleteFacets(facetsData);
  const searchQuery = getNatoriActiveSearchQuery(query);

  attributes.forEach((attribute) => {
    if (attribute === 'options.color') return;
    const values = facetsData[attribute] || {};
    let valueEntries = Object.entries(values);

    const isSizeFacet =
      attribute === 'options.size' ||
      attribute === 'meta.shopify.size' ||
      attribute.endsWith('.size') ||
      attribute === 'options.band' ||
      attribute === 'options.cup' ||
      attribute === 'meta.shopify.cup-size';

    if (isSizeFacet) {
      const sizeOrder = [
        'xxs', 'xs', 's', 'sm', 'small', 'm', 'md', 'medium', 'l', 'lg', 'large',
        'xl', 'x-large', 'xxl', '2xl', 'xxxl', '3xl', '1x', '2x', '3x', '4x', '5x',
        'os', 'one size', 'onesize', 'o/s',
      ];
      valueEntries = valueEntries.sort(([a], [b]) => {
        const aKey = String(a).trim().toLowerCase();
        const bKey = String(b).trim().toLowerCase();
        const aIndex = sizeOrder.indexOf(aKey);
        const bIndex = sizeOrder.indexOf(bKey);
        const aRank = aIndex > -1 ? aIndex : 1000 + (parseFloat(aKey) || 0);
        const bRank = bIndex > -1 ? bIndex : 1000 + (parseFloat(bKey) || 0);
        if (aRank !== bRank) return aRank - bRank;
        return aKey.localeCompare(bKey, undefined, { numeric: true, sensitivity: 'base' });
      });
    } else {
      valueEntries = valueEntries.sort(([, aCount], [, bCount]) => bCount - aCount);
    }

    valueEntries = valueEntries.slice(0, 6);

    items.push({
      __facetGroup: true,
      label: getNatoriFacetLabel(attribute).toUpperCase(),
    });

    if (valueEntries.length) {
      valueEntries.forEach(([value, count]) => {
        const label = String(value).replace(/^\[\"|\"\]$/g, '');
        items.push({
          attribute,
          value: label,
          count,
          label,
          url: buildNatoriFacetSearchUrl(searchQuery, attribute, label),
        });
      });
    }
  });

  return items;
};

const clearNatoriActiveCollectionClasses = () => {
  document
    .querySelectorAll('.natori-aa-collection--active')
    .forEach((el) => el.classList.remove('natori-aa-collection--active'));
};

const markNatoriActiveCollectionInDom = (handle) => {
  clearNatoriActiveCollectionClasses();
  if (!handle) return;

  const safeHandle =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(handle)
      : String(handle).replace(/"/g, '\\"');

  document
    .querySelectorAll(
      `.natori-aa-panel__suggestions .aa-ItemLink[data-natori-collection-handle="${safeHandle}"]`
    )
    .forEach((link) => {
      link.closest('.aa-Item')?.classList.add('natori-aa-collection--active');
    });
};

const setNatoriActiveCollection = ({ handle, title, query }) => {
  const nextQuery = String(query || handle || title || '')
    .trim()
    .toLowerCase();
  if (!nextQuery) return false;

  const same =
    natoriAaActiveCollection &&
    natoriAaActiveCollection.query === nextQuery &&
    natoriAaActiveCollection.handle === handle;

  natoriAaActiveCollection = {
    handle: handle || nextQuery,
    title: title || handle || nextQuery,
    query: nextQuery,
  };

  markNatoriActiveCollectionInDom(natoriAaActiveCollection.handle);
  return !same;
};

const refreshNatoriAutocompleteForActiveCollection = () => {
  if (typeof natoriAaApi?.refresh === 'function') {
    natoriAaApi.refresh();
  }
};

const createNatoriAutocompleteFacetsPlugin = () => {
  const { searchClient } = window.algoliaShopify;
  const { getAlgoliaResults } = window.algoliaShopify.externals;

  return {
    name: 'natori-autocomplete-facets',
    getSources({ query }) {
      const searchQuery = getNatoriActiveSearchQuery(query);
      if (!searchQuery) {
        return [];
      }

      return [
        {
          sourceId: 'natoriFacets',
          getItems() {
            return getNatoriProductsIndexName().then((indexName) => {
              const stockFilter = getNatoriStockFilter();
              const searchParams = {
                hitsPerPage: 0,
                facets: ['*'],
                maxValuesPerFacet: 6,
              };

              if (stockFilter) {
                searchParams.filters = stockFilter;
              }

              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName,
                    query: searchQuery,
                    params: searchParams,
                  },
                ],
                transformResponse({ results }) {
                  const facetsData = results[0]?.facets || {};
                  return buildNatoriAutocompleteFacetItems(facetsData, searchQuery);
                },
              });
            });
          },
          getItemUrl({ item }) {
            return item.__facetGroup ? undefined : item.url;
          },
          templates: {
            item({ item, html }) {
              if (item.__facetGroup) {
                return html`<span class="natori-aa-facet__title">${item.label}</span>`;
              }
              return html`<a
                class="natori-aa-facet__value"
                href="${item.url}"
              >${item.label}</a>`;
            },
          },
        },
      ];
    },
  };
};

const setupNatoriCollectionHover = () => {
  if (window.__natoriAaCollectionHoverBound) {
    return;
  }
  window.__natoriAaCollectionHoverBound = true;

  document.addEventListener(
    'mouseover',
    (event) => {
      const link = event.target.closest?.(
        '.natori-aa-panel__suggestions .aa-ItemLink[data-natori-collection-handle]'
      );
      if (!link) return;

      const handle = link.getAttribute('data-natori-collection-handle') || '';
      const title = link.getAttribute('data-natori-collection-title') || link.textContent || '';
      if (!handle && !title) return;

      if (natoriAaHoverTimer) {
        clearTimeout(natoriAaHoverTimer);
      }

      natoriAaHoverTimer = setTimeout(() => {
        const changed = setNatoriActiveCollection({
          handle,
          title: title.trim(),
          query: handle || title.trim(),
        });
        if (changed) {
          refreshNatoriAutocompleteForActiveCollection();
        } else {
          markNatoriActiveCollectionInDom(handle);
        }
      }, 180);
    },
    true
  );
};

const setupAlgoliaAutocompleteOptions = () => {
  let isClosingAlgoliaSearch = false;
  let natoriAaPanelResizeBound = false;

  setupNatoriCollectionHover();

  const updateNatoriAutocompletePanelMaxHeight = () => {
    const panel = document.querySelector(
      '.thb-quick-search--form .aa-Panel, .thb-quick-search .aa-Panel, .aa-Panel'
    );

    if (!panel) {
      return;
    }

    // When panel lives under quick-search, drop Algolia's body-anchored inline offsets.
    if (panel.closest('.thb-quick-search')) {
      panel.style.top = '';
      panel.style.left = '';
      panel.style.right = '';
      panel.style.width = '';
      panel.style.maxWidth = '';
    }

    const panelTop = panel.getBoundingClientRect().top;
    const available = Math.max(160, window.innerHeight - panelTop - 8);
    panel.style.maxHeight = `${available}px`;
  };

  const bindNatoriAutocompletePanelResize = () => {
    if (natoriAaPanelResizeBound) {
      return;
    }

    natoriAaPanelResizeBound = true;
    window.addEventListener('resize', updateNatoriAutocompletePanelMaxHeight);
  };

  const unbindNatoriAutocompletePanelResize = () => {
    if (!natoriAaPanelResizeBound) {
      return;
    }

    natoriAaPanelResizeBound = false;
    window.removeEventListener('resize', updateNatoriAutocompletePanelMaxHeight);
  };

  const setAlgoliaAutocompleteBodyScroll = (isOpen) => {
    document.body.classList.toggle('overflow-hidden', isOpen);
    document.body.classList.toggle('aa-autocomplete-open', isOpen);
  };

  const closeAlgoliaAutocompletePanel = (autocompleteRoot) => {
    if (isClosingAlgoliaSearch) {
      return;
    }

    isClosingAlgoliaSearch = true;
    natoriAaActiveCollection = null;
    clearNatoriActiveCollectionClasses();

    const input = autocompleteRoot?.querySelector('.aa-Input');
    if (input) {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.blur();
    }

    autocompleteRoot?.classList.remove('aa-Autocomplete--open');
    autocompleteRoot?.querySelector('.aa-Panel')?.classList.remove('aa-Panel--open');
    autocompleteRoot?.querySelector('.aa-DetachedOverlay')?.classList.remove('aa-DetachedOverlay--open');
    document.querySelector('.header-section')?.classList.remove('search-open');
    setAlgoliaAutocompleteBodyScroll(false);

    queueMicrotask(() => {
      isClosingAlgoliaSearch = false;
    });
  };

  if (!window.__algoliaAutocompleteClearBound) {
    window.__algoliaAutocompleteClearBound = true;
    document.addEventListener('click', (event) => {
      const clearButton = event.target.closest('.aa-ClearButton');
      if (!clearButton) {
        return;
      }

      event.preventDefault();
      closeAlgoliaAutocompletePanel(clearButton.closest('.aa-Autocomplete'));
    });
  }

  algoliaShopify.hooks.registerHook('beforeAutocompletePluginsArray', function () {
    const plugins = Array.prototype.slice.call(arguments);

    return plugins.filter((plugin) => {
      if (!plugin || !plugin.getSources) {
        return true;
      }

      const sources = plugin.getSources({ query: '' });
      if (!sources || !sources.length) {
        return true;
      }

      return !sources.some((source) => source.sourceId === 'articles' || source.sourceId === 'pages');
    });
  });

  // When a collection is hovered, re-query products (collections stay on typed query).
  algoliaShopify.hooks.registerHook('beforeAutocompleteConfigurationOptions', (config, context) => {
    const activeQuery = natoriAaActiveCollection?.query;
    if (!activeQuery) {
      return config;
    }

    const sourceId = context?.sourceId || '';
    const pluginName = context?.pluginName || '';
    const isProducts =
      sourceId === 'products' ||
      pluginName === 'products' ||
      sourceId === 'natoriFacets' ||
      pluginName === 'natori-autocomplete-facets';

    if (!isProducts || !Array.isArray(config?.queries)) {
      return config;
    }

    config.queries = config.queries.map((query) => ({
      ...query,
      query: activeQuery,
    }));

    return config;
  });

  algoliaShopify.hooks.registerHook('beforeAutocompletePluginOptions', (options, context) => {
    if (context.pluginName === 'products') {
      options.searchParameters = {
        ...(options.searchParameters || {}),
        hitsPerPage: 4,
      };
    }

    return options;
  });

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteProductTemplate',
    (_defaultTemplate, templateOptions, distinct, itemLink, trackSearchAttribution) => {
      const { html, item, components } = templateOptions;
      const image = buildNatoriAutocompleteProductImage(item);
      const title = item.title || '';

      return html`
        <a
          href="${itemLink}"
          class="aa-ItemLink"
          onClick="${() => trackSearchAttribution(item)}"
        >
          <div class="aa-ItemWrapper">
            <div class="aa-ItemContent">
              <div class="aa-ItemPicture aa-ItemPicture--loaded">
                <img
                  src="${image.src}"
                  srcset="${image.srcset}"
                  sizes="${image.sizes}"
                  alt="${title}"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div class="aa-ItemContentBody">
                <div class="aa-ItemContentTitle">
                  ${components.Highlight({ hit: item, attribute: 'title' })}
                  <span class="algolia-variant">
                    ${algoliaShopify.helpers.variantTitleAddition(item, distinct)}
                  </span>
                </div>
                <div class="aa-ItemContentPrice">
                  ${algoliaShopify.helpers.displayPrice(item, distinct)}
                </div>
              </div>
            </div>
          </div>
        </a>
      `;
    }
  );

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteCollectionsTemplate',
    (_defaultTemplate, templateOptions, itemLink) => {
      const { html, item, components } = templateOptions;
      const handle =
        item.handle ||
        String(itemLink || '')
          .split('/collections/')[1]
          ?.split(/[?#]/)[0] ||
        '';
      const title = item.title || '';

      return html`
        <a
          href="${itemLink}"
          class="aa-ItemLink"
          data-natori-collection-handle="${handle}"
          data-natori-collection-title="${title}"
        >
          <div class="aa-ItemWrapper">
            <div class="aa-ItemContent">
              <div class="aa-ItemContentBody">
                <div class="aa-ItemContentTitle">
                  ${components.Highlight({ hit: item, attribute: 'title' })}
                </div>
              </div>
            </div>
          </div>
        </a>
      `;
    }
  );

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteMainTemplate',
    (_defaultTemplate, templateOptions, elements, displaySuggestions) => {
      const { html } = templateOptions;

      // Re-apply sticky active class after Autocomplete re-renders the panel.
      queueMicrotask(() => {
        if (natoriAaActiveCollection?.handle) {
          markNatoriActiveCollectionInDom(natoriAaActiveCollection.handle);
        }
      });

      return html`
        <div class="aa-PanelLayout natori-aa-panel">
          <div class="natori-aa-panel__sections">
            <div class="natori-aa-panel__suggestions">
              ${displaySuggestions ? elements.querySuggestionsPlugin : ''}
              ${elements.collections}
            </div>
            <div class="natori-aa-panel__facets">
              ${elements.natoriFacets}
            </div>
            <div class="natori-aa-panel__products">
              ${elements.products}
            </div>
          </div>
        </div>
      `;
    }
  );

  algoliaShopify.hooks.registerHook('beforeAutocompleteHeaderTemplate', (_defaultTemplate, templateOptions) => {
    const { html } = templateOptions;
    return html``;
  });

  algoliaShopify.hooks.registerHook('beforeAutocompleteFooterTemplate', (_defaultTemplate, templateOptions) => {
    const { html } = templateOptions;
    return html``;
  });

  algoliaShopify.hooks.registerHook('beforeAutocompleteOptions', (options) => {
    options.plugins = options.plugins || [];
    options.plugins.push(createNatoriAutocompleteFacetsPlugin());

    // Keep autocomplete inside .thb-quick-search on mobile (match production quick-search),
    // instead of Algolia's full-screen .aa-DetachedContainer.
    options.detachedMediaQuery = 'none';

    // Append panel under quick-search (like Searchspring's absolute .ss__autocomplete),
    // not <body>, so theme positioning/CSS apply and the close button stays on the input row.
    const panelContainer =
      document.querySelector('.thb-quick-search--form') ||
      document.querySelector('.thb-quick-search');
    if (panelContainer) {
      options.panelContainer = panelContainer;
    }

    options.placeholder = "Search for anything";
    options.translations = {
      ...(options.translations || {}),
      clearButtonTitle: 'Close search',
    };

    const originalOnStateChange = options.onStateChange;
    options.onStateChange = (params) => {
      natoriAaApi = {
        refresh: params?.refresh,
        setQuery: params?.setQuery,
        setContext: params?.setContext,
      };

      const nextQuery = params?.state?.query || '';
      if (nextQuery !== natoriAaLastInputQuery) {
        // Typing a new query clears the hovered collection filter.
        natoriAaActiveCollection = null;
        clearNatoriActiveCollectionClasses();
        natoriAaLastInputQuery = nextQuery;
      }

      const isOpen = Boolean(params?.state?.isOpen);
      setAlgoliaAutocompleteBodyScroll(isOpen);

      const headerSection = document.querySelector('.header-section');
      if (isOpen) {
        headerSection?.classList.add('search-open');
        bindNatoriAutocompletePanelResize();
        requestAnimationFrame(updateNatoriAutocompletePanelMaxHeight);
        if (natoriAaActiveCollection?.handle) {
          requestAnimationFrame(() => {
            markNatoriActiveCollectionInDom(natoriAaActiveCollection.handle);
          });
        }
      } else {
        unbindNatoriAutocompletePanelResize();
        natoriAaActiveCollection = null;
        clearNatoriActiveCollectionClasses();
      }

      if (typeof originalOnStateChange === 'function') {
        originalOnStateChange(params);
      }
    };

    const originalOnReset = options.onReset;
    options.onReset = (params) => {
      if (typeof originalOnReset === 'function') {
        originalOnReset(params);
      }

      closeAlgoliaAutocompletePanel(params?.autocomplete?.getRootNode?.());
    };

    return options;
  });
};

document.addEventListener('algolia.hooks.initialize', () => {
  try {
    setupAlgoliaAutocompleteOptions();
  } catch (error) {
    console.error('Algolia autocomplete options setup failed', error);
  }
});
